import { useRef, useEffect, useState, FormEvent } from 'react';

type SubmitStatus = 'idle' | 'loading' | 'success' | 'error';

export default function Contact() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea');
    if (textarea && textarea.value.length > 500) return;
    setStatus('loading');

    const formData = new FormData(form);
    const body = new URLSearchParams();
    formData.forEach((value, key) => body.append(key, value as string));

    try {
      const res = await fetch('https://readdy.ai/api/form/d70irfuth28qd804crqg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="w-full bg-[#181818] py-32 px-10 md:px-20">
      <div className="max-w-screen-xl mx-auto">
        <div
          ref={ref}
          className={`flex flex-col lg:flex-row gap-20 xl:gap-32 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {/* ── Left ── */}
          <div className="lg:w-5/12 flex-shrink-0 flex flex-col justify-center">
            <span className="font-sans-kr text-[10px] tracking-[0.3em] uppercase text-white/40 mb-8 block">
              Contact
            </span>

            <h2 className="font-korean-serif text-[2.6rem] md:text-[3.2rem] text-white font-light leading-[1.25] mb-8">
              프로젝트를<br />
              함께 시작해봐요.
            </h2>

            <div className="w-8 h-px bg-white/20 mb-8" />

            <p className="font-sans-kr text-[13px] text-white/50 font-light leading-[1.9] mb-10">
              프로젝트 문의, 협업 제안, 또는 궁금한 점이 있으시면<br />
              편하게 연락주세요. 모든 문의에 성실하게 답변드립니다.
            </p>

            <div>
              <p className="font-sans-kr text-[10px] tracking-[0.2em] uppercase text-white/30 mb-2">
                이메일
              </p>
              <a
                href="mailto:hello@dslv.kr"
                className="font-sans-kr text-[15px] text-white/70 hover:text-white transition-all duration-400 cursor-pointer"
              >
                hello@dslv.kr
              </a>
            </div>
          </div>

          {/* ── Right ── */}
          <div className="lg:w-7/12">
            {status === 'success' ? (
              /* Success State */
              <div className="flex flex-col items-center justify-center h-full py-24 text-center">
                <div className="w-14 h-14 border border-white/30 flex items-center justify-center mb-10">
                  <i className="ri-check-line text-xl text-white/60" />
                </div>
                <p className="font-korean-serif text-[1.6rem] text-white font-light mb-3">
                  메시지가 전달되었습니다
                </p>
                <p className="font-sans-kr text-sm text-white/40 font-light mb-10">
                  곧 연락드리겠습니다.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="font-sans-kr text-xs text-white/40 hover:text-white/70 transition-all duration-400 cursor-pointer border-b border-white/20 hover:border-white/50 pb-0.5 whitespace-nowrap"
                >
                  다시 문의하기
                </button>
              </div>
            ) : (
              /* Form */
              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="space-y-8"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="flex flex-col gap-2">
                    <label className="font-sans-kr text-[10px] tracking-[0.2em] uppercase text-white/40">
                      이름
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="홍길동"
                      className="bg-transparent border-b border-white/20 pb-3 font-sans-kr text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-white/60 transition-all duration-400"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="font-sans-kr text-[10px] tracking-[0.2em] uppercase text-white/40">
                      이메일
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="hello@example.com"
                      className="bg-transparent border-b border-white/20 pb-3 font-sans-kr text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-white/60 transition-all duration-400"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="font-sans-kr text-[10px] tracking-[0.2em] uppercase text-white/40">
                    문의 유형
                  </label>
                  <select
                    name="inquiry_type"
                    required
                    className="bg-transparent border-b border-white/20 pb-3 font-sans-kr text-sm text-white/70 focus:outline-none focus:border-white/60 transition-all duration-400 cursor-pointer appearance-none"
                  >
                    <option value="" className="bg-[#181818]">선택해 주세요</option>
                    <option value="콘텐츠 제작" className="bg-[#181818]">콘텐츠 제작</option>
                    <option value="포맷 개발" className="bg-[#181818]">포맷 개발</option>
                    <option value="브랜드 협업" className="bg-[#181818]">브랜드 협업</option>
                    <option value="기타" className="bg-[#181818]">기타</option>
                  </select>
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <label className="font-sans-kr text-[10px] tracking-[0.2em] uppercase text-white/40">
                      메시지
                    </label>
                    <span className={`font-sans-kr text-[10px] ${charCount > 500 ? 'text-red-400' : 'text-white/30'}`}>
                      {charCount} / 500
                    </span>
                  </div>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="프로젝트에 대해 간략히 말씀해 주세요..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="bg-transparent border-b border-white/20 pb-3 font-sans-kr text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-white/60 transition-all duration-400 resize-none"
                  />
                </div>

                {status === 'error' && (
                  <p className="font-sans-kr text-xs text-red-400">
                    전송에 실패했습니다. 다시 시도해 주세요.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'loading' || charCount > 500}
                  className="font-sans-kr text-xs tracking-[0.2em] uppercase text-white border border-white/40 px-10 py-4 hover:bg-white hover:text-[#181818] transition-all duration-400 cursor-pointer disabled:opacity-30 whitespace-nowrap"
                >
                  {status === 'loading' ? '전송 중...' : '문의 보내기'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
